import { useState, useMemo } from 'react';
import { D, st, toArr, fmt, fmtDate } from '../constants';
import { Overlay, MHdr, Field, StatGrid, getDirections } from './shared';

const STATUS_COLOR = {"Needs Scheduling":"#E24B4A", Scheduled:"#378ADD", "In Progress":"#f0a030", Completed:"#1D9E75", "On Hold":"#888"};
const STATUS_BG = {"Needs Scheduling":"#FCEBEB", Scheduled:"#E6F1FB", "In Progress":"#FBF1E1", Completed:"#E1F5EE", "On Hold":"#F1EFE8"};
const JOB_STATUSES = ['Needs Scheduling','Scheduled','In Progress','Completed','On Hold'];
const RECUR_ICONS = {Insurance:"🛡️", "Rent/Lease":"🏢", Utilities:"💡", Vehicle:"🚗", Subscription:"📱", "Loan Payment":"💳", Equipment:"🔧", Other:"💰"};

const JobBadge = ({status}) =>
  <span style={{background:STATUS_BG[status]||"#eee",color:STATUS_COLOR[status]||"#888",
    fontSize:11,fontWeight:500,padding:"2px 8px",borderRadius:6,
    border:`0.5px solid ${STATUS_COLOR[status]||"#ccc"}`}}>{status}</span>;

function JobModal({data, clients, estimates, onSave, onClose}) {
  const [m, setM] = useState(data);
  const [saving, setSaving] = useState(false);
  const clientEstimates = cid => toArr(estimates).filter(e => e.clientId === cid);
  const save = async () => {
    if (!m.title.trim()) return;
    setSaving(true);
    try { await onSave(m); } finally { setSaving(false); }
  };
  return (
    <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(520px,95vw)"}}>
      <MHdr title={m.id?"Edit Job":"New Job"} onClose={onClose}/>
      <Field label="Title"><input style={st.input} value={m.title} onChange={e=>setM(x=>({...x,title:e.target.value}))}/></Field>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <Field label="Client">
          <select style={st.input} value={m.clientId||''} onChange={e=>setM(x=>({...x,clientId:e.target.value?Number(e.target.value):null,estimateId:null}))}>
            <option value="">- None -</option>
            {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Linked Estimate">
          <select style={st.input} value={m.estimateId||''} onChange={e=>setM(x=>({...x,estimateId:e.target.value?Number(e.target.value):null}))} disabled={!m.clientId}>
            <option value="">- None -</option>
            {clientEstimates(m.clientId).map(e=><option key={e.id} value={e.id}>{e.number}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select style={st.input} value={m.status} onChange={e=>setM(x=>({...x,status:e.target.value}))}>
            {JOB_STATUSES.map(s=><option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Date"><input type="date" style={st.input} value={m.date} onChange={e=>setM(x=>({...x,date:e.target.value}))}/></Field>
        <Field label="Time"><input type="time" style={st.input} value={m.time} onChange={e=>setM(x=>({...x,time:e.target.value}))}/></Field>
        <Field label="End Date"><input type="date" style={st.input} value={m.endDate} onChange={e=>setM(x=>({...x,endDate:e.target.value}))}/></Field>
        <div style={{gridColumn:"1/-1"}}><Field label="Address"><input style={st.input} value={m.address} onChange={e=>setM(x=>({...x,address:e.target.value}))}/></Field></div>
        <div style={{gridColumn:"1/-1"}}><Field label="Description"><input style={st.input} value={m.description} onChange={e=>setM(x=>({...x,description:e.target.value}))}/></Field></div>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
        <button style={st.btn()} onClick={onClose}>Cancel</button>
        <button style={st.btn("primary")} disabled={saving||!m.title.trim()} onClick={save}>{saving?'Saving...':'Save'}</button>
      </div>
    </div></Overlay>
  );
}

export default function Calendar({jobs, clients, estimates, recurring, clientName, apiSaveJob, apiDeleteJob}) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [jobModal, setJobModal] = useState(null);
  const [listView, setListView] = useState(false);
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const prevMonth = () => { if (viewMonth===0) { setViewMonth(11); setViewYear(y=>y-1); } else setViewMonth(m=>m-1); setSelectedDay(null); };
  const nextMonth = () => { if (viewMonth===11) { setViewMonth(0); setViewYear(y=>y+1); } else setViewMonth(m=>m+1); setSelectedDay(null); };
  const daysInMonth = (y,m) => new Date(y,m+1,0).getDate();
  const firstDayOfMonth = (y,m) => new Date(y,m,1).getDay();
  const pad = n => String(n).padStart(2,"0");
  const dateStr = (y,m,d) => `${y}-${pad(m+1)}-${pad(d)}`;

  const jobsByDate = useMemo(() => { const map={}; toArr(jobs).forEach(j=>{ if(!map[j.date])map[j.date]=[]; map[j.date].push(j); }); return map; }, [jobs]);

  const recurByDate = useMemo(() => {
    const map = {};
    toArr(recurring).filter(b => b.active !== 0).forEach(b => {
      const day = parseInt(b.dueDay) || 1;
      if (b.frequency === "Weekly") {
        const targetDow = parseInt(b.dueDay) || 0;
        let cur = new Date(viewYear, viewMonth, 1);
        while (cur.getDay() !== targetDow) cur.setDate(cur.getDate()+1);
        while (cur.getMonth() === viewMonth) {
          const wds = `${viewYear}-${pad(viewMonth+1)}-${pad(cur.getDate())}`;
          if (!map[wds]) map[wds] = []; map[wds].push(b);
          cur.setDate(cur.getDate()+7);
        }
        return;
      }
      if (b.frequency === "Annually") return; // no specific date stored for annual bills
      const cd = Math.min(day, daysInMonth(viewYear, viewMonth));
      const ds = `${viewYear}-${pad(viewMonth+1)}-${pad(cd)}`;
      if (!map[ds]) map[ds] = []; map[ds].push(b);
    });
    return map;
  }, [recurring, viewYear, viewMonth]);

  const totalDays = daysInMonth(viewYear, viewMonth);
  const firstDay = firstDayOfMonth(viewYear, viewMonth);
  const cells = [];
  for (let i=0; i<firstDay; i++) cells.push(null);
  for (let d=1; d<=totalDays; d++) cells.push(d);

  const openNewJob = (dateOverride=null) => {
    const defaultDate = dateOverride || new Date().toISOString().slice(0,10);
    setJobModal({id:null, clientId:clients[0]?.id||null, estimateId:null, title:"", date:defaultDate, time:"08:00", endDate:"", status:"Scheduled", address:clients[0]?.address||"", description:""});
  };
  const openEditJob = job => setJobModal({...job});
  const saveJob = async d => { await apiSaveJob(d); setJobModal(null); };
  const deleteJob = id => { if (confirm('Delete this job?')) apiDeleteJob(id); };

  const selectedDateStr = selectedDay ? dateStr(viewYear, viewMonth, selectedDay) : null;
  const selectedJobs = selectedDateStr ? toArr(jobsByDate[selectedDateStr]) : [];
  const selectedRecur = selectedDateStr ? toArr(recurByDate[selectedDateStr]) : [];
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`;

  const upcomingJobs = useMemo(() => toArr(jobs).filter(j => j.status!=="Completed" && j.status!=="On Hold").sort((a,b)=>(a.date||"").localeCompare(b.date||"")||(a.time||"").localeCompare(b.time||"")), [jobs]);
  const allSortedJobs = useMemo(() => toArr(jobs).slice().sort((a,b)=>(b.date||"").localeCompare(a.date||"")||(a.time||"").localeCompare(b.time||"")), [jobs]);
  const fmtTime = t => { if (!t) return ""; const [h,m] = t.split(":"); const hr = parseInt(h); return `${hr>12?hr-12:hr||12}:${m} ${hr>=12?"PM":"AM"}`; };

  const upcomingRecur = useMemo(() => {
    const todayDate = new Date(todayStr+"T00:00:00");
    const results = [];
    toArr(recurring).filter(b => b.active !== 0 && b.frequency !== "Annually").forEach(b => {
      const day = parseInt(b.dueDay) || 1;
      const y = todayDate.getFullYear(), m = todayDate.getMonth();
      const cd = Math.min(day, new Date(y,m+1,0).getDate());
      const thisMonth = new Date(y,m,cd);
      let ds;
      if (thisMonth >= todayDate) ds = `${y}-${pad(m+1)}-${pad(cd)}`;
      else { const nm = m+1>11?0:m+1, ny = m+1>11?y+1:y; const cd2 = Math.min(day, new Date(ny,nm+1,0).getDate()); ds = `${ny}-${pad(nm+1)}-${pad(cd2)}`; }
      const diff = Math.round((new Date(ds+"T00:00:00") - todayDate) / 86400000);
      if (diff >= 0 && diff <= 30) results.push({...b, nextDs:ds, days:diff});
    });
    return results.sort((a,b) => a.days - b.days);
  }, [recurring, todayStr]);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div style={st.h1}>Calendar</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button style={{...st.btn(),padding:"6px 12px",background:!listView?D.accent:D.border,color:!listView?"#fff":D.text}} onClick={()=>setListView(false)}>Month</button>
          <button style={{...st.btn(),padding:"6px 12px",background:listView?D.accent:D.border,color:listView?"#fff":D.text}} onClick={()=>setListView(true)}>List</button>
          <button style={st.btn("primary")} onClick={()=>openNewJob()}>+ Schedule Job</button>
        </div>
      </div>

      <StatGrid stats={[
        {label:"Upcoming jobs", value:upcomingJobs.length, color:D.accent},
        {label:"This month", value:toArr(jobs).filter(j=>{ if(!j.date) return false; const d=new Date(j.date+"T00:00:00"); return d.getMonth()===today.getMonth() && d.getFullYear()===today.getFullYear(); }).length, color:D.warning},
        {label:"Completed", value:toArr(jobs).filter(j=>j.status==="Completed").length, color:D.success},
        {label:"Bills due ≤7d", value:upcomingRecur.filter(b=>b.days<=7).length, color:D.danger},
      ]}/>

      {listView ? (
        <div style={st.card}>
          <div style={st.h2}>All Jobs</div>
          {!allSortedJobs.length ? <div style={{color:D.muted,textAlign:"center",padding:"32px 0"}}>No jobs scheduled yet.</div> : (
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Date","Time","Title","Client","Address","Status","Linked To",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {allSortedJobs.map(j => {
                  const est = toArr(estimates).find(e=>e.id===j.estimateId);
                  return (
                    <tr key={j.id}>
                      <td style={st.td}>{fmtDate(j.date)}</td>
                      <td style={{...st.td,fontSize:12,color:D.muted,whiteSpace:"nowrap"}}>{j.time?fmtTime(j.time):""}</td>
                      <td style={{...st.td,fontWeight:500}}>{j.title}</td>
                      <td style={st.td}>{j.clientId?clientName(j.clientId):"-"}</td>
                      <td style={{...st.td,fontSize:12,color:D.muted,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.address||"—"}</td>
                      <td style={st.td}><JobBadge status={j.status}/></td>
                      <td style={{...st.td,fontSize:12}}>{est ? <span style={{color:D.accent}}>{est.number}</span> : <span style={{color:D.muted}}>—</span>}</td>
                      <td style={st.td}>
                        <button style={st.btn()} onClick={()=>openEditJob(j)}>Edit</button>
                        <button style={st.btn("danger")} onClick={()=>deleteJob(j.id)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:20}}>
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <button style={st.btn()} onClick={prevMonth}>‹ Prev</button>
              <div style={{fontWeight:500,fontSize:16}}>{monthNames[viewMonth]} {viewYear}</div>
              <button style={st.btn()} onClick={nextMonth}>Next ›</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
              {dayNames.map(d=><div key={d} style={{textAlign:"center",fontSize:11,color:D.muted,padding:"4px 0",fontWeight:500}}>{d}</div>)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
              {cells.map((day,i) => {
                if (day === null) return <div key={`empty-${i}`}/>;
                const ds = dateStr(viewYear, viewMonth, day);
                const dayJobs = toArr(jobsByDate[ds]);
                const dayBills = toArr(recurByDate[ds]);
                const isToday = ds === todayStr, isSelected = ds === selectedDateStr;
                return (
                  <div key={ds} onClick={()=>setSelectedDay(isSelected?null:day)}
                    style={{minHeight:76,background:isSelected?D.accent+"22":isToday?D.accent+"11":D.surface,border:`1px solid ${isSelected?D.accent:isToday?D.accent+"55":D.border}`,borderRadius:8,padding:"6px 8px",cursor:"pointer"}}>
                    <div style={{fontSize:12,fontWeight:isToday?700:400,color:isToday?D.accent:D.text,marginBottom:3}}>{day}</div>
                    {dayJobs.slice(0,2).map(j=>(
                      <div key={j.id} style={{fontSize:10,background:(STATUS_COLOR[j.status]||D.muted)+"33",color:STATUS_COLOR[j.status]||D.muted,padding:"1px 5px",borderRadius:3,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"pointer"}} onClick={e=>{e.stopPropagation();openEditJob(j);}}>{j.time?fmtTime(j.time)+" ":""}{j.title}</div>
                    ))}
                    {dayBills.slice(0,2).map(b=>(
                      <div key={b.id} style={{fontSize:10,background:D.danger+"22",color:D.danger,padding:"1px 5px",borderRadius:3,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>💳 {b.description}</div>
                    ))}
                    {(dayJobs.length+dayBills.length) > 3 && <div style={{fontSize:9,color:D.muted}}>+{dayJobs.length+dayBills.length-3} more</div>}
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",gap:16,marginTop:10,fontSize:11,color:D.muted}}>
              <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:D.accent+"55",display:"inline-block"}}/>Jobs</span>
              <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:D.danger+"33",display:"inline-block"}}/>Bills due</span>
            </div>
          </div>
          <div style={{width:270,flexShrink:0}}>
            {selectedDay ? (
              <div style={st.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={{fontWeight:500,fontSize:14}}>{monthNames[viewMonth]} {selectedDay}</div>
                  <button style={{...st.btn("primary"),padding:"4px 10px",fontSize:11}} onClick={()=>openNewJob(selectedDateStr)}>+ Job</button>
                </div>
                {selectedJobs.length===0 && selectedRecur.length===0 && <div style={{color:D.muted,fontSize:13,textAlign:"center",padding:"20px 0"}}>Nothing scheduled.</div>}
                {selectedJobs.map(j => {
                  const est = toArr(estimates).find(e=>e.id===j.estimateId);
                  return (
                    <div key={j.id} style={{background:D.surface,borderRadius:8,padding:"10px 12px",marginBottom:10,border:`1px solid ${(STATUS_COLOR[j.status]||D.muted)}55`}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><div style={{fontWeight:500,fontSize:13}}>{j.title}</div><JobBadge status={j.status}/></div>
                      {j.clientId && <div style={{fontSize:12,color:D.muted,marginBottom:4}}>{clientName(j.clientId)}</div>}
                      {j.time && <div style={{fontSize:12,color:D.accent,marginBottom:4}}>⏰ {fmtTime(j.time)}</div>}
                      {j.address && <div style={{fontSize:11,color:D.muted,marginBottom:4,display:"flex",alignItems:"center",gap:6}}>📍 {j.address}<button onClick={()=>getDirections(j.address)} style={{background:"none",border:`1px solid ${D.accent}`,color:D.accent,borderRadius:5,fontSize:10,padding:"1px 7px",cursor:"pointer",fontWeight:600,flexShrink:0}}>Directions</button></div>}
                      {j.description && <div style={{fontSize:11,color:D.muted,marginBottom:6,fontStyle:"italic"}}>{j.description}</div>}
                      {est && <div style={{fontSize:11,marginBottom:6}}><span style={{color:D.accent}}>📋 {est.number}</span></div>}
                      <div style={{display:"flex",gap:6,marginTop:6}}>
                        <button style={{...st.btn(),padding:"4px 10px",fontSize:11,marginLeft:0}} onClick={()=>openEditJob(j)}>Edit</button>
                        <button style={{...st.btn("danger"),padding:"4px 10px",fontSize:11}} onClick={()=>deleteJob(j.id)}>Delete</button>
                      </div>
                    </div>
                  );
                })}
                {selectedRecur.map(b => (
                  <div key={b.id} style={{background:D.surface,borderRadius:8,padding:"10px 12px",marginBottom:10,border:`1px solid ${D.danger}44`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <div style={{fontWeight:500,fontSize:13}}>{RECUR_ICONS[b.category]||"💰"} {b.description}</div>
                      <span style={{fontSize:14,fontWeight:700,color:D.warning}}>{fmt(Number(b.amount)||0)}</span>
                    </div>
                    <div style={{fontSize:11,color:D.muted}}>{b.category} · {b.frequency}</div>
                    {b.vendor && <div style={{fontSize:11,color:D.muted,marginTop:2}}>Vendor: {b.vendor}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={st.card}>
                <div style={{fontWeight:500,fontSize:14,marginBottom:14}}>Upcoming</div>
                {upcomingJobs.length===0 && upcomingRecur.length===0 && <div style={{color:D.muted,fontSize:13,textAlign:"center",padding:"20px 0"}}>Nothing upcoming.</div>}
                {upcomingJobs.slice(0,5).map(j => (
                  <div key={j.id} style={{borderBottom:`1px solid ${D.border}`,padding:"8px 0",cursor:"pointer"}} onClick={()=>{ if(!j.date) return; const d=new Date(j.date+"T00:00:00"); setViewMonth(d.getMonth()); setViewYear(d.getFullYear()); setSelectedDay(d.getDate()); }}>
                    <div style={{fontWeight:500,fontSize:13}}>{j.title}</div>
                    <div style={{fontSize:11,color:D.muted}}>{fmtDate(j.date)}{j.time?` · ${fmtTime(j.time)}`:""}</div>
                    {j.clientId && <div style={{fontSize:11,color:D.muted}}>{clientName(j.clientId)}</div>}
                  </div>
                ))}
                {upcomingRecur.slice(0,5).map(b => (
                  <div key={b.id} style={{borderBottom:`1px solid ${D.border}`,padding:"8px 0",cursor:"pointer"}} onClick={()=>{ const d=new Date(b.nextDs+"T00:00:00"); setViewMonth(d.getMonth()); setViewYear(d.getFullYear()); setSelectedDay(d.getDate()); }}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{fontWeight:500,fontSize:13}}>{RECUR_ICONS[b.category]||"💰"} {b.description}</div>
                      <span style={{fontSize:12,fontWeight:600,color:b.days<=3?D.danger:b.days<=7?D.warning:D.muted}}>{b.days===0?"Today":b.days+"d"}</span>
                    </div>
                    <div style={{fontSize:11,color:D.muted}}>{fmtDate(b.nextDs)} · {fmt(Number(b.amount)||0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {jobModal && <JobModal data={jobModal} clients={clients} estimates={estimates} onSave={saveJob} onClose={()=>setJobModal(null)}/>}
    </div>
  );
}
